import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { NavigateFunction } from 'react-router-dom';
import api, { clearApiCache } from '../../api/base';
import { userData } from '../../api/paths';
import localStorageKeys from '../../constants/localStorageKeys';
import { addSnackbar } from './snackbar';

interface Memberships {
  id: string;
  name: string;
  internal_type_name: string;
  nodes: Array<string>;
}

export interface UserData {
  id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  import_key: string;
  volume_alerts: number;
  memberships: Memberships;
  active_organization: {
    id: string;
    name: string;
    audited: boolean;
  };
  accepted_security_policy: boolean;
  accepted_privacy_policy: boolean;
  accepted_terms_of_use: boolean;
  completed_tutorial: boolean;
  groups: {
    id: string;
    name: string;
  }[];
  permissions: {
    action: string;
    name: string;
  }[];
  settings: {
    name: string;
    value: any;
  }[];
  is_staff: boolean;
}

type TokensType = {
  access: string;
  refresh: string;
  ws: string;
};

type RefreshTokens = {
  access: string;
  refresh: string;
};

interface InitialData {
  user: UserData | null;
  tokens: {
    access: string | null;
    refresh: string | null;
    ws: string | null;
  };
  mapsLoaded: boolean;
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string;
  staffShowConfidence: boolean;
}

interface TermsData {
  token: string | null;
  accepted_security_policy?: boolean;
  accepted_privacy_policy?: boolean;
  accepted_terms_of_use?: boolean;
}

const initialState: InitialData = {
  user: null,
  tokens: {
    access: localStorage.getItem(localStorageKeys.viprToken) || null,
    refresh: localStorage.getItem(localStorageKeys.viprRefreshToken) || null,
    ws: localStorage.getItem(localStorageKeys.viprWSToken) || null,
  },
  mapsLoaded: false,
  loading: 'idle',
  error: '',
  staffShowConfidence: localStorage.getItem('staffShowConfidence') !== 'false',
};

type GetUserProfilePayload = {
  token: string;
  navigate: NavigateFunction;
};
export const getUserProfileAction = createAsyncThunk(
  'getUserProfile',
  async (payload: GetUserProfilePayload, thunkAPI) => {
    const { token, navigate } = payload;
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    try {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      const response = await api.get(userData);
      const data = await response.data;

      switch (false) {
        case data.accepted_security_policy:
          navigate('/security-terms');
          break;
        case data.accepted_privacy_policy:
          navigate('/privacy-policy');
          break;
        case data.accepted_terms_of_use:
          navigate('/terms-of-use');
          break;
        default:
          navigate('/dashboard');
      }
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

export const acceptPoliciesAction = createAsyncThunk(
  'acceptPolicy',
  async (termsData: TermsData, thunkAPI) => {
    try {
      const response = await api.patch(userData, termsData, {
        headers: {
          Authorization: `Bearer ${termsData.token}`,
        },
      });
      const data = await response.data;
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

export const changeUserVolume = createAsyncThunk(
  'changeVolume',
  async (volume: number, thunkAPI) => {
    try {
      const response = await api.patch(userData, { volume_alerts: volume });
      const data = await response.data;
      return data;
    } catch (error: any) {
      const err = error.response?.data?.detail;
      return thunkAPI.dispatch(addSnackbar({ message: err }));
    }
  },
);

export const userProfileSlice = createSlice({
  name: 'user-profile',
  initialState,
  reducers: {
    logoutUser: (state) => {
      localStorage.removeItem(localStorageKeys.viprToken);
      localStorage.removeItem(localStorageKeys.viprRefreshToken);
      localStorage.removeItem(localStorageKeys.viprWSToken);
      localStorage.removeItem(localStorageKeys.permissions);
      clearApiCache();
      delete api.defaults.headers.common.Authorization;

      state.user = initialState.user;
      state.tokens = {
        access: null,
        refresh: null,
        ws: null,
      };
      state.loading = initialState.loading;
      state.error = initialState.error;
    },
    setUserTokens: (state, action: PayloadAction<TokensType>) => {
      localStorage.setItem(localStorageKeys.viprToken, action.payload.access);
      localStorage.setItem(
        localStorageKeys.viprRefreshToken,
        action.payload.refresh,
      );
      localStorage.setItem(localStorageKeys.viprWSToken, action.payload.ws);
      state.tokens = action.payload;
    },
    setRefreshedToken: (state, action: PayloadAction<RefreshTokens>) => {
      state.tokens.access = action.payload.access;
      state.tokens.refresh = action.payload.refresh;
    },
    updateUserPhone: (state, action: PayloadAction<string>) => {
      state.user!.phone_number = action.payload;
    },
    updateUserTutorFlag: (state) => {
      state.user!.completed_tutorial = true;
    },
    updateUser: (state, action: PayloadAction<UserData>) => {
      state.user = action.payload;
      localStorage.setItem(
        localStorageKeys.permissions,
        JSON.stringify(action.payload.permissions.map((perm) => perm.action)),
      );
    },
    updateMapsStatus: (state, action: PayloadAction<boolean>) => {
      state.mapsLoaded = action.payload;
    },
    toggleStaffConfidence: (state) => {
      const next = !state.staffShowConfidence;
      state.staffShowConfidence = next;
      localStorage.setItem('staffShowConfidence', String(next));
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserProfileAction.pending, (state) => {
      state.loading = 'pending';
    });
    builder.addCase(getUserProfileAction.fulfilled, (state, action: any) => {
      state.loading = 'succeeded';
      state.user = action.payload;
      localStorage.setItem(
        localStorageKeys.permissions,
        JSON.stringify(action.payload.permissions.map((perm) => perm.action)),
      );
    });
    builder.addCase(getUserProfileAction.rejected, (state, action: any) => {
      state.loading = 'succeeded';
      state.error = action.payload.error;
    });
    builder.addCase(acceptPoliciesAction.fulfilled, (state, action: any) => {
      state.loading = 'succeeded';
      state.user = action.payload;
    });
    builder.addCase(changeUserVolume.fulfilled, (state, action: any) => {
      state.loading = 'succeeded';
      state.user = action.payload;
    });
  },
});

export default userProfileSlice.reducer;
export const {
  logoutUser,
  setUserTokens,
  updateUserPhone,
  setRefreshedToken,
  updateUserTutorFlag,
  updateUser,
  updateMapsStatus,
  toggleStaffConfidence,
} = userProfileSlice.actions;
